/**
 * Fluxo estruturado: professor avisa UM responsável sobre UM aluno (templates fixos).
 */
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Modal } from '../../components/ui/Modal';
import {
  useMotivosAviso,
  useResponsaveisAviso,
  useAvisarResponsavel,
  type MotivoAvisoId,
} from '../../api/hooks/useAvisosPedagogicos';

interface Props {
  open: boolean;
  onClose: () => void;
  alunoId: string;
  alunoNome: string;
}

export function AvisarResponsavelModal({ open, onClose, alunoId, alunoNome }: Props) {
  const { data: motivos = [], isLoading: loadingMotivos } = useMotivosAviso();
  const { data: responsaveis = [], isLoading: loadingResp } = useResponsaveisAviso(
    alunoId,
    open,
  );
  const avisar = useAvisarResponsavel();

  const [motivo, setMotivo] = useState<MotivoAvisoId | ''>('');
  const [responsavelId, setResponsavelId] = useState('');
  const [complemento, setComplemento] = useState('');

  useEffect(() => {
    if (!open) return;
    setMotivo('');
    setComplemento('');
    setResponsavelId('');
  }, [open, alunoId]);

  useEffect(() => {
    if (!responsaveis.length) return;
    const principal = responsaveis.find((r) => r.principal && r.recebeNotificacao);
    const fallback = responsaveis.find((r) => r.recebeNotificacao) ?? responsaveis[0];
    setResponsavelId(principal?.id ?? fallback?.id ?? '');
  }, [responsaveis]);

  const motivoLabel = useMemo(
    () => motivos.find((m) => m.id === motivo)?.label ?? '',
    [motivos, motivo],
  );

  const previewTexto = useMemo(() => {
    if (!motivo || !alunoNome) return '';
    const comp = complemento.trim()
      ? ` Observação registrada: ${complemento.trim()}.`
      : '';
    return (
      `A escola informa que foi registrado: ${motivoLabel.toLowerCase()}, ` +
      `referente ao(a) aluno(a) ${alunoNome}.${comp} ` +
      `Para mais detalhes, a família pode contatar a coordenação.`
    );
  }, [motivo, motivoLabel, alunoNome, complemento]);

  const handleEnviar = async () => {
    if (!motivo) {
      toast.error('Selecione o tipo de aviso.');
      return;
    }
    if (!responsavelId) {
      toast.error('Selecione um responsável.');
      return;
    }
    try {
      await avisar.mutateAsync({
        alunoId,
        responsavelId,
        motivo,
        complemento: complemento.trim() || undefined,
      });
      toast.success('Aviso enviado ao responsável.');
      onClose();
    } catch (e: unknown) {
      const d =
        e && typeof e === 'object' && 'response' in e
          ? (e as { response?: { data?: { message?: string } } }).response?.data
          : undefined;
      toast.error(
        typeof d?.message === 'string' ? d.message : 'Não foi possível enviar o aviso.',
      );
    }
  };

  const loading = loadingMotivos || loadingResp;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Avisar responsável"
      contentClassName="ac-comunicado-modal"
      dialogId="modal-avisar-responsavel"
      footer={
        <>
          <button type="button" className="btn-cancel" onClick={onClose}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn-save"
            disabled={avisar.isPending || loading}
            onClick={() => void handleEnviar()}
          >
            {avisar.isPending ? 'Enviando...' : 'Enviar aviso'}
          </button>
        </>
      }
    >
      <p style={{ fontSize: '0.75rem', color: '#64748b', margin: '0 0 12px' }}>
        Aluno: <strong>{alunoNome}</strong>
      </p>

      {loading ? (
        <p style={{ textAlign: 'center', color: '#94a3b8', fontSize: '0.8rem' }}>Carregando...</p>
      ) : responsaveis.length === 0 ? (
        <p style={{ color: '#dc2626', fontSize: '0.78rem' }}>
          Nenhum responsável vinculado a este aluno.
        </p>
      ) : (
        <>
          <div className="form-group">
            <label className="form-label" htmlFor="aviso-responsavel">
              Responsável *
            </label>
            <select
              id="aviso-responsavel"
              className="form-select"
              value={responsavelId}
              onChange={(e) => setResponsavelId(e.target.value)}
            >
              {responsaveis.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.nome}
                  {r.principal ? ' (principal)' : ''}
                  {!r.recebeNotificacao ? ' — sem notificação' : ''}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="aviso-motivo">
              Tipo de aviso *
            </label>
            <select
              id="aviso-motivo"
              className="form-select"
              value={motivo}
              onChange={(e) => setMotivo(e.target.value as MotivoAvisoId)}
            >
              <option value="">Selecione...</option>
              {motivos.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.label}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="aviso-complemento">
              Complemento (opcional, máx. 120 caracteres)
            </label>
            <input
              id="aviso-complemento"
              className="form-input"
              maxLength={120}
              value={complemento}
              onChange={(e) => setComplemento(e.target.value)}
              placeholder="Ex.: 1ª ocorrência na semana"
              autoComplete="off"
            />
          </div>

          {previewTexto && (
            <div
              style={{
                marginTop: 8,
                padding: 10,
                borderRadius: 8,
                background: '#f8fafc',
                border: '1px solid #e2e8f0',
                fontSize: '0.72rem',
                color: '#475569',
                lineHeight: 1.45,
              }}
            >
              <div style={{ fontWeight: 700, marginBottom: 4, color: '#334155' }}>
                Pré-visualização (texto enviado ao responsável)
              </div>
              {previewTexto}
            </div>
          )}
        </>
      )}
    </Modal>
  );
}
